// src/pages/Accounting/Reports/AccountList/components/ReportToolbar.tsx
// Second row of the Account List report page.
// Zoom + density control (left) · Refresh/Email/Print/Export/More/Ask Vela (right).
// 3-dot and zoom are self-contained components with their own open state.

import React from 'react';
import { RefreshCw, Mail, Printer, Download } from 'lucide-react';
import { fmtRelativeTime } from '../utils';
import ZoomDropdown from './ZoomDropdown';
import ThreeDotMenu from './ThreeDotMenu';

interface ReportToolbarProps {
  lastRefreshedAt: Date | null;
  isRefreshing: boolean;
  density: 'compact' | 'standard' | 'spacious';
  zoom: number;
  onDensityChange: (d: 'compact' | 'standard' | 'spacious') => void;
  onZoomChange: (z: number) => void;
  onRefresh: () => void;
  onEmail: () => void;
  onPrint: () => void;
  onExport: () => void;
  onScheduleReport: () => void;
  onAddToManagementReports: () => void;
  onAskVela: () => void;
}

export default function ReportToolbar(props: ReportToolbarProps) {
  const refreshTip = props.isRefreshing ? 'Refreshing...' : fmtRelativeTime(props.lastRefreshedAt);

  return (
    <div className="flex justify-between items-center pt-3">
      <ZoomDropdown
        density={props.density}
        zoom={props.zoom}
        onDensityChange={props.onDensityChange}
        onZoomChange={props.onZoomChange}
      />

      <div className="flex items-center gap-1">
        <IconButton tooltip={refreshTip} onClick={props.onRefresh}>
          <RefreshCw size={15} strokeWidth={2} className={props.isRefreshing ? 'animate-spin' : ''} />
        </IconButton>
        <IconButton tooltip="Email" onClick={props.onEmail}>
          <Mail size={15} strokeWidth={2} />
        </IconButton>
        <IconButton tooltip="Print" onClick={props.onPrint}>
          <Printer size={15} strokeWidth={2} />
        </IconButton>
        <IconButton tooltip="Export" onClick={props.onExport}>
          <Download size={15} strokeWidth={2} />
        </IconButton>
        <ThreeDotMenu
          onScheduleReport={props.onScheduleReport}
          onAddToManagementReports={props.onAddToManagementReports}
        />
        <button
          type="button"
          onClick={props.onAskVela}
          className="inline-flex items-center gap-1.5 h-8 px-3 ml-1 rounded-md border border-teal-dark bg-white text-teal-dark text-[11.5px] font-bold cursor-pointer hover:bg-[#F4FAF7]"
        >
          <span className="w-[14px] h-[14px] rounded-full bg-gradient-to-br from-[#7C3AED] to-[#6B46C1] text-white text-[8px] font-extrabold inline-flex items-center justify-center">
            V
          </span>
          Ask Vela
        </button>
      </div>
    </div>
  );
}

function IconButton({ tooltip, onClick, children }: { tooltip: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        className="w-8 h-8 rounded-md text-ink3 inline-flex items-center justify-center border-none bg-transparent cursor-pointer hover:bg-panel"
        aria-label={tooltip}
      >
        {children}
      </button>
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-ink text-white text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap pointer-events-none z-50">
        {tooltip}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ink" />
      </span>
    </div>
  );
}